import {AfterViewInit, Component, forwardRef, Input, ViewChild} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {Modal, ModalController} from 'ionic-angular';
import {QuillEditorComponent} from 'ngx-quill';
import Quill from 'quill';
import {AbstractInput} from '../../../../../shared/components/inputs/abstract/abstract.input';
import {QuillEditorContentChangedInterface} from '../../../interfaces/quill-editor-content-changed.interface';
import {ShortcodeInterface} from '../../../interfaces/shortcode.interface';
import {BodyInputModalComponent} from './body-input-modal';
import {QuillToolbarComponent} from './quill-toolbar';
import {HrBlot} from './quill/blots/hr/hr.blot';
import {BlockShortcodeBlot} from './quill/blots/shortcode/block/block.shortcodes.blot';
import {DropcapShortcodeBlot} from './quill/blots/shortcode/block/dropcap/dropcap.shortcode.blot';
import {ImageShortcodeBlot} from './quill/blots/shortcode/image/image.shortcode.blot';
import {InlineShortcodeBlot} from './quill/blots/shortcode/inline/inline.shortcode.blot';
import {TableBlot} from './quill/blots/table/table.blot';
import {Image} from './quill/formats/image/image.format';
import {Strike} from './quill/formats/strike/strike.format';

import {IOSSafariQuillEditorModule} from './quill/modules/ios-editor.quill.module';

/**
 * Component to hold the [Quill Editor](https://quilljs.com/ Quill Editor) and facilitate the full screen rte.
 * Utilizes the [ngx-quill](https://github.com/KillerCodeMonkey/ngx-quill) package
 * @class
 * @extends AbstractInput
 * @implements {AfterViewInit}
 *
 * @example
 *
 * ```html
 * <advtech-body-input
 *   #bodyinput
 *   formControlName="body"
 *   [showToolbar]="false"
 *   [modalHeader]="contentEntity.getHeadline().getValue()"
 *   [advtechFieldValidation]="{contentType: 'article', inputTagName:'.ql-editor', eventName: 'input'}"
 * ></advtech-body-input>
 * ```
 *
 */
@Component({
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BodyInput),
    },
  ],
  selector: 'advtech-body-input',
  templateUrl: './body.input.html',
})
export class BodyInput extends AbstractInput implements AfterViewInit {

  /**
   * Body modal instance
   * @type {Modal}
   */
  bodyModal: Modal;

  /**
   * An instance of the QuillEditorComponent
   * @type {QuillEditorComponent}
   */
  @ViewChild('quill')
  quillEditor: QuillEditorComponent;

  /**
   * The instance of the QuillToolbarComponent
   * @type {QuillToolbarComponent}
   */
  @ViewChild('quillToolbar')
  toolbar: QuillToolbarComponent;

  /** @inheritdoc */
  data: any;

  /**
   * Input to make the editor readonly
   * @default
   * @type {boolean}
   */
  @Input()
  readOnly = true;

  /**
   * Input to do modal open on clicking the body
   * @default
   * @type {boolean}
   */
  @Input()
  doModalOpen = true;

  /**
   * Input to show the toolbar or not
   * @default
   * @type {boolean}
   */
  @Input()
  showToolbar = true;

  /**
   * Input to show the body label or not
   * @default
   * @type {boolean}
   */
  @Input()
  showLabel = true;

  /**
   * Input to set the placeholder on the quill editor
   * @default
   * @type {boolean}
   */
  @Input()
  placeholder = 'Click to edit';

  /**
   * Input to set the title display on the modal
   * @default
   * @type {boolean}
   */
  @Input()
  modalHeader: string;

  /** @inheritdoc */
  constructor(public modalCtrl: ModalController) {
    super();
  }

  /** @inheritdoc */
  ngAfterViewInit() {
    if (this.data) {
      this.setValue(this.data.value || this.data);
    }
    if (this.doModalOpen) {
      this.quillEditor.onContentChanged.subscribe((res: QuillEditorContentChangedInterface) => {
        const container = res.editor.root;
        const modelValue = this.getModelValue(container);
        this.onChange(modelValue);
        this.dispatchChangeEvent();
      });
    }
    this.toolbar && this.toolbar.init(this.quillEditor.quillEditor);
  }

  /**
   * Dispatches the change event for the quill editor
   * @return {void}
   */
  dispatchChangeEvent(): void {
    const evt = document.createEvent('HTMLEvents');
    evt.initEvent('change', false, true);
    this.quillEditor.quillEditor.root.dispatchEvent(evt);
  }

  /**
   * Sets the value in the quill editor
   * @param {string} value
   * @return {BodyInput}
   */
  setValue(value: string): this {
    if (value) {
      super.setValue(value);
      this.quillEditor.quillEditor.pasteHTML(value);
      this.dispatchChangeEvent();
    }
    return this;
  }

  /**
   * Gets the value of the Quill editor
   * @returns {string}
   */
  getValue(): string {
    const container = this.quillEditor.quillEditor.container.querySelector('.ql-editor') as HTMLElement;
    const modelValue = this.getModelValue(container);
    return modelValue;
  }

  getModelValue(container) {
    let html = container.innerHTML;
    const shortcodes = container.querySelectorAll('[data-shortcode]');
    Array.from(shortcodes).forEach((node: Element) => {
      const shortcodeBlot: ShortcodeInterface = Quill.find(node);
      const newNode = shortcodeBlot.getModelTag();
      html = html.replace(node.outerHTML, newNode.outerHTML);
    });

    return html;
  }

  /**
   * get the quill config
   * @return {object}
   */
  getQuillModuleConfig(): {} {
    this.registerQuillExtras({
      blots: [
        BlockShortcodeBlot,
        InlineShortcodeBlot,
        DropcapShortcodeBlot,
        ImageShortcodeBlot,
        TableBlot,
        HrBlot,
      ],
      formats: [
        Strike,
        Image,
      ],
      modules: [
        {
          name: 'ios_editor',
          class: IOSSafariQuillEditorModule,
        },
      ],
    });
    return {
      toolbar: this.getToolbarConfig(),
      ios_editor: this.showToolbar,
      clipboard: {
        matchVisual: false,
      },
    };
  }

  /**
   * Handles the click event in the body input. If doModalOpen is set to true it will open the [[BodyInputModal]]
   * @param {Event} e
   * @returns {void}
   */
  hClick(e: Event): void {
    if (this.doModalOpen) {
      this.bodyModal = this.modalCtrl.create(BodyInputModalComponent, {
        title: this.modalHeader,
        value: this.getValue(),

      });
      this.bodyModal.onDidDismiss((val) => {
        if (val) {
          this.setValue(val.value);
        }
      });
      this.bodyModal.present({keyboardClose: false});
    }
  }

  /**
   * Registers quill extras like modules and blots
   * @param {{blots: [], formats: [], modules: Array<{name, class}>}} obj Blots and modules
   * @returns {void}
   */
  registerQuillExtras({blots, formats, modules}): void {
    this.registerBlots(blots);
    this.registerModules(modules);
    this.registerFormats(formats);
    Quill.debug('');
  }

  /**
   * Register custom formats
   * @param {any[]} formats array of format classes
   * @returns {void}
   */
  registerFormats(formats: any[]): void {
    for (const format of formats) {
      Quill.register(format, true);
    }
  }

  /**
   * Register custom blots
   * @param {any[]} blots array of blot classes
   * @returns {void}
   */
  registerBlots(blots: any[]): void {
    for (const blot of blots) {
      Quill.register(blot);
    }
  }

  /**
   * Registers custom modules
   * @param {Array<{name: string, object: {}}>} modules li
   * @returns {void}
   */
  registerModules(modules) {
    for (const module of modules) {
      Quill.register(`modules/${module.name}`, module.class);
    }
  }

  /**
   * Sets the disabled state
   * @param {boolean} isDisabled should disable
   * @returns {boolean}
   */
  setDisabledState(isDisabled: boolean): boolean {
    return true;
  }

  /**
   * Returns the RTE Toolbar config
   * @returns {object}
   */
  protected getToolbarConfig() {

    if (!this.showToolbar) {
      return false;
    }

    return '#toolbar';
  }
}



// WEBPACK FOOTER //
// ./src/app/content/shared/components/inputs/body/body.input.ts